import React from 'react';
import { Node } from '@tiptap/core';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { lookupScript, adminBasePath } from './tmeWikiNodeSupport';

/**
 * An inline reference to a Script Repository script.
 *
 * Planning/TME-WIKI-2026-09-02.md §7, §9.3, D5, D23, R15.
 *
 * ⚠️ THE STORED MARKUP IS A PLAIN ANCHOR, AND EVERY PART OF THAT IS DELIBERATE:
 *
 *     <a href="/admin/scripts/script_01H…">Export mailbox sizes</a>
 *
 *   - `a` and `href` are on the sanitiser's allow-list, and the relative path
 *     matches its ALLOWED_URI_REGEXP, so the markup survives a round trip
 *     unchanged;
 *   - NO `class` and NO `data-*`: a data- attribute is stripped, and a new class
 *     would loosen a set SHARED with the client wiki;
 *   - ⚠️ NO `target`. The sanitiser FORCES `rel="noopener noreferrer"` onto any
 *     element carrying `target`, so a node emitting one comes back with an extra
 *     attribute and the round trip is no longer byte-identical.
 *
 * ⚠️ THE INLINE NODE IS DISPLAY ONLY - THE LINK ROW IS THE RECORD (D5). Removing
 * this node from the body does NOT unlink the script; unlinking happens in the
 * Links panel, so a stray keystroke cannot silently break the Script
 * Repository's "used by" list.
 */

export const SCRIPT_HREF_PREFIX = '/admin/scripts/';

/** The React view: renders the LIVE name, and intercepts the click (D23, R15). */
const ScriptRefView = ({ node }) => {
    const { scriptId, label } = node.attrs;
    const live = lookupScript(scriptId);
    const missing = !live;

    const open = (event) => {
        // Without preventDefault the browser follows the href natively and
        // leaves the SPA - on preview, to a path outside `/gantt`.
        event.preventDefault();
        window.open(`${adminBasePath()}${SCRIPT_HREF_PREFIX}${scriptId}`, '_blank');
    };

    return (
        <NodeViewWrapper as="span" className="inline">
            <a
                href={`${SCRIPT_HREF_PREFIX}${scriptId}`}
                onClick={open}
                title={missing ? 'This script no longer exists' : 'Open in the Script Repository'}
                className={missing
                    ? 'text-gray-400 line-through'
                    : 'text-primary-600 underline hover:text-primary-800'}
            >
                {/* LIVE first, the stored label only as the fallback (D23). */}
                {live ? live.name : `${label || scriptId} (deleted)`}
            </a>
        </NodeViewWrapper>
    );
};

const ScriptRefNode = Node.create({
    name: 'scriptRef',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
        return {
            scriptId: { default: null },
            label: { default: '' }
        };
    },

    parseHTML() {
        return [{
            // ⚠️ THE PRIORITY IS ON THE PARSE RULE, NOT ON THE EXTENSION, and
            // it is load-bearing. Link's rule is `a[href]`, which matches these
            // anchors too; ProseMirror's DOMParser orders rules by their OWN
            // `priority` (default 50), so at the default the MARK wins - the
            // anchor renders with Link's class, `target` and `rel`, the node
            // never forms, and the click is not intercepted. Measured, not
            // assumed: an extension-level priority changed nothing.
            priority: 1000,
            tag: `a[href^="${SCRIPT_HREF_PREFIX}"]`,
            getAttrs: (element) => {
                const href = element.getAttribute('href') || '';
                const scriptId = href.slice(SCRIPT_HREF_PREFIX.length);
                if (!scriptId) return false;
                return { scriptId, label: element.textContent || '' };
            }
        }];
    },

    renderHTML({ node }) {
        // EXACTLY the anchor above - no class, no data-*, no target.
        return ['a', { href: `${SCRIPT_HREF_PREFIX}${node.attrs.scriptId}` }, node.attrs.label || ''];
    },

    addNodeView() {
        return ReactNodeViewRenderer(ScriptRefView);
    },

    addCommands() {
        return {
            insertScriptRef: attrs => ({ commands }) =>
                commands.insertContent({ type: this.name, attrs })
        };
    }
});

export default ScriptRefNode;
